/**
 * The four things the demo's own header did that no screen owns.
 *
 *   - a search that goes to a work order or an asset and centres the drawing
 *     on it, rather than filtering a list;
 *   - export and import of the work order register as JSON;
 *   - the DAYLIGHT view, which is a whole second palette in the stylesheet
 *     (`body.lightview`) and not an inversion — every state colour keeps its
 *     meaning, because an operator should not have to relearn that red means
 *     outside limits because the sun came up.
 *
 * These sit in the application header because they apply everywhere, which is
 * where the demo had them too.
 */
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadDemo } from "./domain/load";
import { saveFile } from "../lib/saveFile";
import type { WorkOrder } from "./domain/workOrders";
import * as mapHost from "./domain/mapHost";
import type { Theme } from "../layout/theme";
import { t } from "../i18n";

/**
 * Wait until the drawing screen has mounted and attached its canvas.
 *
 * Loading the rasters is not enough: centring measures the canvas, and a search
 * started from another screen resolves the rasters long before React has
 * rendered the one that owns it. Bounded, so a search that never reaches the
 * drawing gives up quietly instead of polling for ever.
 */
function whenDrawingReady(timeoutMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    const deadline = Date.now() + timeoutMs;
    const tick = () => {
      if (mapHost.cv) return resolve(true);
      if (Date.now() > deadline) return resolve(false);
      requestAnimationFrame(tick);
    };
    tick();
  });
}

interface Asset {
  id: string;
  name: string;
  E: number;
  N: number;
}

/**
 * The theme is passed in rather than read with the hook: `useTheme` holds its
 * own state, so calling it here as well would give the shell and this toolbar
 * two copies — and the pressed state of one would stop matching the other the
 * first time either was used.
 */
export function DemoToolbar({ theme, setTheme }: { theme: Theme; setTheme: (t: Theme) => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const light = theme === "light";
  const [note, setNote] = useState("");
  const file = useRef<HTMLInputElement | null>(null);

  /*
   * A work order by its id, or an asset by name then id. The demo zooms in to
   * at least 6 before centring, because centring at the fitted scale puts the
   * thing in the middle of a view where it is a pixel.
   */
  /*
   * The work register lives in the demo domain, which is a lazy chunk — so
   * every action here resolves it first. These are all things somebody clicked,
   * and by the time they click, a demo screen has usually warmed it already.
   */
  const go = async () => {
    const query = q.trim().toUpperCase();
    if (!query) return;
    /* the survey comes with the domain — 1,044 assets and the sheet they sit on
       are not worth loading to render a page that never shows them */
    const demo = await loadDemo();
    const WOS = demo.WOS as WorkOrder[];
    const list = demo.ASSETS as Asset[];

    const o = WOS.find((w) => w.id.toUpperCase() === query);
    const a = o
      ? null
      : list.find((x) => x.name?.toUpperCase() === query) ||
        list.find((x) => x.name?.toUpperCase().includes(query)) ||
        list.find((x) => x.id.toUpperCase() === query);

    if (!o && !a) {
      setNote(t("Nothing on the drawing matches “{q}”.", { q: q.trim() }));
      return;
    }

    setNote("");
    navigate("/drawing");

    /*
     * Everything after this has to wait for the drawing screen.
     *
     * Centring reads the loaded sheet, and a search from any other screen is
     * exactly the case where the rasters are not in yet. renderDetail writes
     * into #paneDetail, which only exists once that screen has mounted — the
     * first version of this called it immediately and threw on the dashboard.
     */
    demo.setSelected(o ? { kind: "wo", o } : { kind: "asset", a });
    void Promise.all([mapHost.loadRasters(), whenDrawingReady()]).then(([, ready]) => {
      if (!ready) return;
      demo.view.k = Math.max(demo.view.k, 6);
      demo.centre(o ? o.E : a!.E, o ? o.N : a!.N);
      demo.draw();
      if (document.getElementById("paneDetail")) demo.renderDetail();
    });
  };

  const exportWork = async () => {
    const WOS = (await loadDemo()).WOS as WorkOrder[];
    const blob = new Blob(
      [JSON.stringify({ airport: "OEMA", exported: new Date().toISOString(), work_orders: WOS }, null, 1)],
      { type: "application/json" },
    );
    await saveFile("oema-work-orders.json", blob);
  };

  const importWork = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = async () => {
      try {
        const j = JSON.parse(String(r.result));
        if (!Array.isArray(j.work_orders)) throw new Error("no work_orders");
        const d = await loadDemo();
        const WOS = d.WOS as WorkOrder[];
        /*
         * Replace the contents rather than the array: every screen in the
         * extracted domain holds a reference to this exact array, and swapping
         * it would leave them all reading the old one.
         */
        WOS.length = 0;
        WOS.push(...j.work_orders);
        setNote(t("Imported {n} work order(s).", { n: j.work_orders.length }));
        d.buildHeat();
        d.renderWos();
        if (document.getElementById("paneDetail")) d.renderDetail();
        d.draw();
      } catch {
        setNote(t("That file isn't a work order export."));
      }
    };
    r.readAsText(f);
    e.target.value = "";
  };

  return (
    <>
      <input
        type="text"
        id="search"
        value={q}
        placeholder="STAND 14 / I27 / WO-004"
        autoComplete="off"
        aria-label={t("Find a work order or an asset")}
        title={note || t("Type a work order id or an asset name, then press Enter")}
        onChange={(e) => {
          setQ(e.target.value);
          if (note) setNote("");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void go();
          }
        }}
        style={{ width: 170 }}
      />

      <div className="seg" role="group" aria-label={t("Display")}>
        <button id="vDay" aria-pressed={light} onClick={() => setTheme("light")}>
          {t("DAYLIGHT")}
        </button>
        <button id="vDark" aria-pressed={!light} onClick={() => setTheme("dark")}>
          {t("DARK")}
        </button>
      </div>

      <button
        className="btn ghost"
        style={{ width: "auto", margin: 0 }}
        onClick={() => void exportWork()}
        title={t("Download the work order register as JSON")}
      >
        {t("EXPORT")}
      </button>

      <label className="file" style={{ margin: 0 }} title={t("Read a work order export back in")}>
        <input type="file" accept="application/json" ref={file} onChange={importWork} />
        <span style={{ padding: "7px 10px", borderStyle: "solid" }}>{t("IMPORT")}</span>
      </label>

      {note && (
        <span className="eyebrow" role="status" style={{ color: "var(--amber)" }}>
          {note}
        </span>
      )}
    </>
  );
}
